<template>
  <div id="orderMenu" v-if="category">
    <vue-loading :active="isLoading" :is-full-page="true" />
    <div v-for="cat in orderedCategories" :key="cat.id">
      <div class="grid-layout">
        <div v-for="product in cat.products" :key="product?.id">
          <ProductContainer
            :product="product"
            :basket-items="basketTile?.basketItems"
            :read-only="!orderingEnabled"
            @update-product="onProductUpdate"
            @edit-product="onProductEdit"
            @view-details="onViewProductDetails"
          />
        </div>
      </div>
    </div>

    <!-- Modal for product details -->
    <b-modal id="product-details-modal" hide-footer :title="selectedProduct ? selectedProduct.name : ''">
      <ProductDetails
        v-if="selectedProduct"
        :productId="selectedProduct.productId"
        :read-only="!orderingEnabled"
        @close="closeProductDetailsModal"
        @product-update="handleProductUpdateWithOptions"
      />
    </b-modal>

    <!-- Modal for basket products -->
    <b-modal id="basket-product-editor-modal" hide-footer :title="selectedProduct ? selectedProduct.name : ''">
      <BasketProductEditor v-if="selectedProduct"
         :basket-items="getBasketItemsByProdId(selectedProduct.productId)"
         @close="closeBasketProductEditorModal"
         @product-quantity-change="onProductQuantityChange"
      />
    </b-modal>
  </div>
</template>

<script>
import ProductDetails from "@/components/sub-components/Product/ProductDetails.vue";
import {basketService} from "@/services/api/basketService";
import {useAppStore} from "@/stores/app";
import {useShopStore} from "@/stores/shop";
import {encode64} from "@/utils/encodeUtils";
import { getFoodPackagingProductsContainer } from "@/utils/productUtils";
import BasketProductEditor from "@/components/sub-components/Basket/BasketProductEditor.vue";
import productDetailsMixin from "@/components/mixins/productDetailsMixin";
import ProductContainer from "@/components/data-display/ProductContainer.vue";
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  mixins: [productDetailsMixin],
  components: {
    ProductContainer,
    BasketProductEditor,
    ProductDetails,
    VueLoading,
  },
  props: {
    category: {
      type: Object,
      required: true,
    },
    basketTile: {
      type: Object,
      required: false,
      default: null
    },
    orderingEnabled: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      selectedProduct: {},
      selectedProductsCount: {}, // { [groupId]: { [productId]: quantity } }
      selectedChildId: null, // Track selected child category
      isLoading: false,
    };
  },
  computed: {
    /** @file public/settings/config/default.js */
    config() {
      return window.config;
    },
    orderedCategories() {
      let categories = [];
      // Only include root category if it has products
      if (this.category && Array.isArray(this.category.products) && this.category.products.length > 0) {
        categories.push({ ...this.category, children: undefined });
      }
      // Only include children that have products
      if (Array.isArray(this.category?.children) && this.category.children.length > 0) {
        const sortedChildren = this.category.children.slice().sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0));
        const childrenWithProducts = sortedChildren.filter(child => Array.isArray(child.products) && child.products.length > 0);
        categories = categories.concat(childrenWithProducts);
      }
      return categories;
    }
  },
  setup() {
    const appStore = useAppStore();
    const shopStore =useShopStore()

    return {
      appStore,
      shopStore
    };
  },
  watch: {
    basketTile: {
      immediate: true,
      handler(newBasketTile) {
        // Reset selectedProductsCount
        const counts = {};
        if (newBasketTile && Array.isArray(newBasketTile.basketItems)) {
          newBasketTile.basketItems.forEach(item => {
            //if (!counts) counts[groupId] = {};
            // Sum quantities for all items with the same prodId (regardless of options)
            if (!counts[item.prodId]) counts[item.prodId] = 0;
            counts[item.prodId] += Number(item.quantity) || 0;
          });
        }
        this.selectedProductsCount = counts;
      }
    },
    category: {
      immediate: true,
      handler(newCategory) {
        // Reset selectedChildId if parent changes
        if (!newCategory || !Array.isArray(newCategory.children) || newCategory.children.length === 0) {
          this.selectedChildId = null;
        } else if (this.selectedChildId && newCategory.children.some(child => child.id === this.selectedChildId)) {
          // keep current if still valid
        } else {
          this.selectedChildId = null;
        }
      }
    }
  },
  methods: {
    getUnavailableArticleMessage(productName) {
      return this.$t('notification.itemUnavailable', [productName || this.$t('system.label.noData')]);
    },
    getProductNameById(productId) {
      const categoryMatch = this.orderedCategories
        .flatMap(cat => cat.products || [])
        .find(product => Number(product?.id) === Number(productId));

      return categoryMatch?.name || categoryMatch?.description || null;
    },
    buildUpdatedBasketTile(result) {
      if (!result?.basketTile) {
        return null;
      }

      const foodPackagingProducts = getFoodPackagingProductsContainer(result) || getFoodPackagingProductsContainer(result.basketTile);

      return {
        ...result.basketTile,
        foodPackagingProducts: foodPackagingProducts || undefined,
      };
    },
    async handleProductUpdateWithOptions(productId, quantity, price, productOptions) {
      if (!this.orderingEnabled) {
        return;
      }

      await this.handleProductUpdate(productId, false, false, '', 'increment', productOptions, quantity);
    },
    async handleProductUpdate(productId, isLocked, hasChildren, name, type, productOptions, quantity = 1) {
      if (!this.orderingEnabled) {
        return;
      }

      if (hasChildren) {
        if (type === 'increment') {
          this.viewProductDetails(productId, isLocked, name);
        }
      } else {
        if (type === 'increment') {
          await this.addToBasket(productId, quantity, productOptions);
        } else if (type === 'decrement') {
          await this.removeFromBasket(productId, quantity);
        }
      }
    },
    /**
     *
     * @param productId
     * @param quantity
     * @param {string[]} productOptions format: `["key=value","key1=value1","keyN=valueN"]`
     * */
    async addToBasket(productId, quantity, productOptions) {
      if (!this.orderingEnabled) {
        return;
      }

      this.isLoading = true;
      try {
        let result, options = null;
        if (Array.isArray(productOptions) && productOptions.length > 0) {
          // Defensive: flatten deeply nested arrays and filter only strings
          const flatOptions = [].concat(...productOptions).filter(opt => typeof opt === 'string');
          options = flatOptions.reduce((acc, option) => {
            const [key, value] = option.split('=');
            acc[key] = value;
            return acc;
          }, { prod: productId });
          options.kkjson = encode64(JSON.stringify(options));
        }

        if (options) {
          result = await basketService.addComplexProductToBasket(productId, this.appStore.currentOrderMenuOrderType, quantity, options);
        } else {
          result = await basketService.addProductToBasket(productId, this.appStore.currentOrderMenuOrderType, quantity);
        }
        if (result.exception) {
          const productName = this.getProductNameById(productId) || this.selectedProduct?.name;

          if (result.exception.code === 64) {
            this.makeToast('danger', this.$t('orderMenu.messages.error.addItemMinError'));
          } else if (result.exception.code === 65) {
            this.makeToast('danger', this.$t('orderMenu.messages.error.addItemMaxError'));
          } else if (result.exception.code === 66) {
            this.makeToast('danger', this.getUnavailableArticleMessage(productName));
          } else {
            this.makeToast('danger', this.$t('orderMenu.messages.error.addItemFailed'));
          }
          return;
        }

        // Emit updated basketTile to parent, watcher will update counts and basket total
        this.$emit('basket-tile-updated', this.buildUpdatedBasketTile(result));

      } catch (error) {
        this.makeToast('danger', this.$t('orderMenu.messages.error.addItemFailed'));
      } finally {
        this.isLoading = false;
      }
    },
    async removeFromBasket(productId, quantity) {
      if (!this.orderingEnabled) {
        return;
      }

      this.isLoading = true;
      try {
        const result = await basketService.removeProductFromBasketByProductId(productId, quantity, this.appStore.currentOrderMenuOrderType);
        if (result.exception) {
          const errorCode = result.exception.code;
          const errorMessages = {
            64: 'orderMenu.messages.error.removeItemMinError',
            65: 'orderMenu.messages.error.removeItemMaxError',
          };
          this.makeToast('danger', this.$t(errorMessages[errorCode] || 'orderMenu.messages.error.removeItemFailed'));
          return;
        }

        // Emit updated basketTile to parent, watcher will update counts and basket total
        this.$emit('basket-tile-updated', this.buildUpdatedBasketTile(result));

      } catch (error) {
        this.makeToast('danger', this.$t('orderMenu.messages.error.removeItemFailed'));
      } finally {
        this.isLoading = false;
      }
    },
    viewProductDetails(productId, isLocked, name) {
      if (!isLocked) {
        this.selectedProduct = { productId, name };
        this.$bvModal.show("product-details-modal");
      }
    },
    getBasketItemsByProdId(prodId) {
      return this.basketTile?.basketItems?.filter(item => item.prodId === prodId) || [];
    },
    openBasketProductEditor(productId, name, isLocked) {
      if (this.orderingEnabled && !isLocked) {
        this.selectedProduct = { productId, name };
        this.$bvModal.show("basket-product-editor-modal");
      }
    },
    closeBasketProductEditorModal() {
      this.$bvModal.hide("basket-product-editor-modal");
    },
    onProductQuantityChange(basketId, newQuantity) {
      if (!this.orderingEnabled) {
        return;
      }

      this.$emit('product-quantity-change', basketId, newQuantity);
    },
    onProductUpdate(payload) {
      const { productId, isLocked, hasChildren, productName, type, value } = payload || {};
      // Delegate to existing logic; value defaults to 1 if not provided.
      this.handleProductUpdate(productId, isLocked, hasChildren, productName, type, undefined, value || 1);
    },
    onProductEdit(payload) {
      const { productId, productName, isLocked } = payload || {};
      this.openBasketProductEditor(productId, productName, isLocked);
    },
    onViewProductDetails(payload) {
      const { productId, isLocked, productName } = payload || {};
      this.viewProductDetails(productId, isLocked, productName);
    },
  },
};
</script>

<style scoped lang="scss">
</style>
